import React from 'react';
import { Helpers } from '@engine/index';
import { t } from '../lib/i18n';

/**
 * AmortizationTable — Shows depreciation analysis for fixed assets.
 * Displays account, name, saldo UB, change, and algorithmic status per asset.
 *
 * Extracted from index.html (previously inline ~lines 624-687).
 *
 * @param {{ rows: Array, config?: object }} props
 *   - rows: asset objects with { id, name, ub, change, hasAccumulatedDepreciation }
 *   - config: optional system config (unused currently, reserved for future)
 */
export default function AmortizationTable({ rows, config }) {
    if (!rows || rows.length === 0) return null;
    return (
        <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm">
            <table className="w-full text-sm text-left border-collapse">
                <thead className="bg-slate-50 dark:bg-slate-800 text-xs uppercase font-semibold">
                    <tr>
                        <th className="px-6 py-3">{t('account')}</th>
                        <th className="px-6 py-3">{t('accountName')}</th>
                        <th className="px-6 py-3 text-right">{t('saldoUB')}</th>
                        <th className="px-6 py-3 text-right">{t('change')}</th>
                        <th className="px-6 py-3 text-center">{t('algoStatus')}</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700 font-mono">
                    {rows.map((asset, i) => {
                        let statusHtml = null;
                        let rowClass = "";

                        if (asset.ub <= 0) {
                            statusHtml = <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300">{t('retired')}</span>;
                            rowClass = "opacity-60";
                        } else {
                            if (asset.change === 0) {
                                if (asset.hasAccumulatedDepreciation) {
                                    statusHtml = <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                        <span className="w-2 h-2 mr-1 rounded-full bg-green-500"></span> {t('deprAccumulated')}
                                    </span>;
                                } else {
                                    statusHtml = <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                                        <span className="w-2 h-2 mr-1 rounded-full bg-red-500"></span> {t('noDepreciation')}
                                    </span>;
                                    rowClass = "bg-red-50 dark:bg-red-900/10";
                                }
                            } else if (asset.change > 0) {
                                if (Math.abs(asset.change - asset.ub) < 10) {
                                    statusHtml = <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">{t('newPurchase')}</span>;
                                } else {
                                    statusHtml = <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">{t('investment')}</span>;
                                }
                            } else {
                                statusHtml = <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                    <span className="w-2 h-2 mr-1 rounded-full bg-green-500"></span> OK
                                </span>;
                            }
                        }

                        return (
                            <tr key={i} className={`hover:bg-slate-50 dark:hover:bg-slate-800 ${rowClass}`}>
                                <td className="px-6 py-4 font-semibold text-slate-600 dark:text-slate-400">{asset.id}</td>
                                <td className="px-6 py-4 dark:text-slate-300">{asset.name}</td>
                                <td className="px-6 py-4 text-right font-medium">{Helpers.formatMoney(asset.ub)}</td>
                                <td className={`px-6 py-4 text-right ${asset.change < 0 ? 'text-green-600 dark:text-green-400' : asset.change > 0 ? 'text-blue-600 dark:text-blue-400' : 'text-slate-400'}`}>
                                    {Helpers.formatMoney(asset.change)}
                                </td>
                                <td className="px-6 py-4 text-center">{statusHtml}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
